import { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { API } from "../App";

// Morning / evening market posts, liquidation spikes and price levels for the
// trade channel. Public market data only; posted where the OKX alerts go.
const MarketPosts = () => {
  const [s, setS] = useState(null);
  const [busy, setBusy] = useState(false);
  const [preview, setPreview] = useState("");

  useEffect(() => {
    axios.get(`${API}/market/settings`).then((r) => setS(r.data)).catch(() => {});
  }, []);

  const save = async () => {
    setBusy(true);
    try {
      const r = await axios.put(`${API}/market/settings`, s);
      setS(r.data);
      toast.success("Market posts saved");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not save");
    }
    setBusy(false);
  };

  const run = async (kind, send) => {
    if (send && !window.confirm(`Post the ${kind} summary to the channel now?`)) return;
    setBusy(true);
    try {
      const r = await axios.post(`${API}/market/preview`, { kind, send });
      setPreview(r.data.text);
      if (send) toast.success("Posted to the channel");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    }
    setBusy(false);
  };

  if (!s) return null;
  const field = "bg-[#0A0A0A] border border-[#27272A] rounded-md px-3 py-2 text-sm text-white outline-none focus:border-[#00C2FF]";
  const check = (k, label) => (
    <label className="flex items-center gap-2 text-sm text-gray-200">
      <input type="checkbox" checked={!!s[k]} onChange={(e) => setS({ ...s, [k]: e.target.checked })} />
      {label}
    </label>
  );

  return (
    <div className="bg-[#111113] border border-[#27272A] rounded-2xl p-4 sm:p-6 space-y-4 mt-4" data-testid="market-posts">
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-bold text-white">Market posts</h2>
        {check("enabled", "Enabled")}
      </div>
      <p className="text-xs text-gray-500">
        Posted to the same channel as the trade alerts. Coins: top 5 by market cap without stablecoins, re-checked
        daily{s.coins ? ` — now ${s.coins.join(", ")}` : ""}. Times are Beirut time.
      </p>

      <div className="space-y-2">
        <div className="flex items-center gap-3 flex-wrap">
          {check("morning_enabled", "Morning summary at")}
          <input className={`${field} w-24 font-mono`} value={s.morning_time || ""}
                 onChange={(e) => setS({ ...s, morning_time: e.target.value })} />
        </div>
        <input className={`${field} w-full`} placeholder="Greeting" value={s.morning_greeting || ""}
               onChange={(e) => setS({ ...s, morning_greeting: e.target.value })} />
        <div className="flex items-center gap-3 flex-wrap">
          {check("evening_enabled", "Evening wrap-up at")}
          <input className={`${field} w-24 font-mono`} value={s.evening_time || ""}
                 onChange={(e) => setS({ ...s, evening_time: e.target.value })} />
        </div>
      </div>

      <div className="border-t border-[#27272A] pt-3 space-y-2">
        <div className="flex items-center gap-3 flex-wrap">
          {check("liq_enabled", "Liquidation alert when more than")}
          <input className={`${field} w-32 font-mono`} type="number" value={s.liq_usd ?? ""}
                 onChange={(e) => setS({ ...s, liq_usd: e.target.value })} />
          <span className="text-xs text-gray-500">USD in 1 hour</span>
        </div>
        <p className="text-[11px] text-gray-500">
          From OKX's public liquidation feed, so OKX only — whole-market figures on news sites are several times
          bigger. At most one post every 2 hours, unless it doubles.
        </p>
      </div>

      <div className="border-t border-[#27272A] pt-3 space-y-1">
        {check("levels_enabled", "Round-number alerts")}
        <p className="text-[11px] text-gray-500">
          Only for coins you have an open futures position or order in on OKX (right now that's whatever you're
          trading — nothing else is posted). Lines about 5% apart: BTC every 5,000, ETH every 100… e.g. "BTC broke
          above 80,000". The same line isn't posted again for 12 hours, and at most one per coin per hour.
        </p>
      </div>

      <div className="border-t border-[#27272A] pt-3 space-y-1">
        <div className="flex items-center gap-3 flex-wrap">
          {check("moves_enabled", "Big move alert at")}
          <input className={`${field} w-20 font-mono`} type="number" step="0.5" value={s.move_pct ?? ""}
                 onChange={(e) => setS({ ...s, move_pct: e.target.value })} />
          <span className="text-xs text-gray-500">% in 1 hour</span>
        </div>
        <p className="text-[11px] text-gray-500">Also only for coins with open futures.</p>
      </div>

      <div className="flex gap-2 flex-wrap">
        <button onClick={save} disabled={busy} className="px-4 py-2 rounded-md bg-[#00C2FF] text-black text-sm font-bold disabled:opacity-50">
          Save market posts
        </button>
        <button onClick={() => run("morning", false)} disabled={busy}
                className="px-3 py-2 rounded-md border border-[#27272A] text-gray-200 text-sm">Preview morning</button>
        <button onClick={() => run("evening", false)} disabled={busy}
                className="px-3 py-2 rounded-md border border-[#27272A] text-gray-200 text-sm">Preview evening</button>
        <button onClick={() => run("morning", true)} disabled={busy}
                className="px-3 py-2 rounded-md border border-[#00E054]/50 text-[#00E054] text-sm">Post morning now</button>
      </div>
      {preview && (
        <pre className="text-xs text-gray-200 whitespace-pre-wrap bg-[#0A0A0A] border border-[#27272A] rounded-lg p-3 font-sans">{preview}</pre>
      )}
    </div>
  );
};

export default MarketPosts;
